import { defineMessages, FormattedMessage, type IntlShape } from 'dibs-react-intl';
import { type ReactElement } from 'react';
import { CM, IN, type MeasurementUnit } from 'dibs-intl/exports/units';

const messages = defineMessages({
    under: {
        id: 'abf.SbSharedAppliedFiltersItem.under',
        defaultMessage: `Under {max}`,
    },
    between: {
        id: 'abf.SbSharedAppliedFiltersItem.between',
        defaultMessage: `{min}–{max}`,
    },
    over: {
        id: 'abf.SbSharedAppliedFiltersItem.over',
        defaultMessage: `Over {min}`,
    },
});

const units: Record<MeasurementUnit, string> = {
    [IN]: 'inch',
    [CM]: 'centimeter',
};
const getMeasurement = (
    value: string | null | undefined,
    measurementUnit: MeasurementUnit,
    shouldConvertValue: boolean
): number | null => {
    if (value === null || value === '0') {
        return null;
    }
    const numericValue = Number(value);
    const values: Record<MeasurementUnit, number> = {
        [IN]: Math.round(numericValue * 10) / 10,
        [CM]: Math.round(numericValue * 2.54 * 10) / 10, // 1 inch = 2.54 cm
    };
    return shouldConvertValue ? values[measurementUnit] : values[IN];
};

const addMeasurementUnit = (
    intl: IntlShape,
    measurementUnit: MeasurementUnit,
    value: number | null
): string | null => {
    if (!value) {
        return null;
    }
    const measurement = intl.formatNumber(value, {
        style: 'unit',
        unit: units[measurementUnit],
    });
    return measurement;
};
const formatMeasurement = ({
    intl,
    min,
    max,
    measurementUnit,
}: {
    intl: IntlShape;
    min: number | null;
    max: number | null;
    measurementUnit: MeasurementUnit;
}): string => {
    if (min === null) {
        return intl.formatMessage(messages.under, {
            max: addMeasurementUnit(intl, measurementUnit, max),
        });
    } else if (max === null) {
        return intl.formatMessage(messages.over, {
            min: addMeasurementUnit(intl, measurementUnit, min),
        });
    }
    return intl.formatMessage(messages.between, {
        min,
        max: addMeasurementUnit(intl, measurementUnit, max),
    });
};

export const sbSharedRefineMenuMessages = {
    minAndUp: ({ min }: { min: ReactElement }): ReactElement => (
        <FormattedMessage
            id="sb.SbSharedAppliedFiltersItem.inputRange.andUp"
            defaultMessage="{min} and up"
            values={{ min }}
        />
    ),
    minMaxFormattedMessage: ({
        isMobile,
        min,
        max,
    }: {
        isMobile?: boolean;
        min: ReactElement | number;
        max: ReactElement | number;
    }): ReactElement => (
        <FormattedMessage
            id="sb.SbSharedAppliedFiltersItem.min.max"
            defaultMessage="{min} {isMobile, select,
                    true {TO}
                    other {to}
                } {max}"
            values={{ min, max, isMobile }}
        />
    ),
    shippingFree: (
        <FormattedMessage
            id="sb.SbSharedAppliedFiltersItem.freeShipping"
            defaultMessage="Free Shipping"
        />
    ),
    shippingOptions: (
        <FormattedMessage
            id="sb.SbSharedAppliedFiltersItem.shippingOptions"
            defaultMessage="Shipping Options"
        />
    ),
    formatMeasurementFromUrlLabel: ({
        intl,
        urlLabel,
        measurementUnit,
        shouldConvertValue,
    }: {
        intl: IntlShape;
        urlLabel: string | null;
        measurementUnit: MeasurementUnit;
        shouldConvertValue: boolean;
    }): string => {
        //Depth,length examples: "[* TO 55]", "[22 TO 55]", "[22 TO *]"
        //Table width examples: "under-20-inches", "between-30-and-40-inches", "over-120-inches"
        const firstWord = urlLabel?.match(/(\[\*|\w+)/g)?.[0];
        const digits = urlLabel?.match(/\d+/g);
        const min = (firstWord === 'under' || firstWord === '[*' ? '0' : digits?.[0]) || null;
        const max =
            (firstWord === 'under' || firstWord === '[*' ? digits?.[0] : digits?.[1]) || null;
        const minValue = getMeasurement(min, measurementUnit, shouldConvertValue);
        const maxValue = getMeasurement(max, measurementUnit, shouldConvertValue);
        return formatMeasurement({ intl, min: minValue, max: maxValue, measurementUnit });
    },
    width: <FormattedMessage id="sb.SbSharedAppliedFiltersItem.width" defaultMessage="Width" />,
};
